// Attendance report section (Phase 2) — a stats block and/or charts rendered
// inside ReportView after the posts, driven by a shared AttendanceStats.
//
// Both groups are registry-driven: the template picks tile and chart keys
// (models/reportTemplate.ts), this file owns how each one looks and when it
// has enough data to be worth printing. A key the data cannot support is
// dropped, not rendered empty — the same "no data → row omitted" rule the
// header fields follow.
//
// Fixed chart dimensions (NOT recharts ResponsiveContainer): the de-risk
// prototype confirmed ResponsiveContainer can measure 0 in the headless
// Puppeteer PDF render, whereas explicit width/height print reliably on A4.
// 520px fits the report content width; the paired small charts are 250px.

import React from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Legend, LabelList,
} from 'recharts';
import { AttendanceStats, TimelineRow } from '../../services/attendanceStatsService';
import {
  AttendanceChartKey, AttendanceTileKey,
  ATTENDANCE_TILE_LABELS, DEFAULT_ATTENDANCE_CHARTS, DEFAULT_ATTENDANCE_TILES,
} from '../../models/reportTemplate';
import './attendanceSection.scss';

interface Props {
  stats: AttendanceStats;
  showStats: boolean;
  showCharts: boolean;
  /** Absent = the section's original fixed set (see the DEFAULT_* constants). */
  tiles?: AttendanceTileKey[];
  chartKeys?: AttendanceChartKey[];
}

const AXIS = '#7e8a78';
const GRID = '#e6e0d2';
const SCHEDULED = '#9ec5a6';
const ATTENDED = '#26923f';
const EARLY = '#e8a92e';
const ON_TIME = '#7cb342';
const LATE = '#ea7c30';
const ABSENT = '#e5b3b2';
const UNLOGGED = '#d8d8d8';

/** More rows than this stop being readable on an A4 page. */
const TIMELINE_MAX_ROWS = 12;

const hhmm = (mins: number): string =>
  `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

// ── Stat tiles ──────────────────────────────────────────────────────────────

/** Each returns the tile value, or null to drop the tile entirely. */
const TILE_VALUES: Record<AttendanceTileKey, (s: AttendanceStats) => string | null> = {
  attendance_rate: (s) => (s.attendanceRate === null ? null : `${s.attendanceRate}%`),
  sessions_attended: (s) => `${s.sessionsAttended}`,
  sessions_scheduled: (s) => `${s.sessionsScheduled}`,
  hours_attended: (s) => `${s.hoursAttended}`,
  hours_scheduled: (s) => `${s.hoursScheduled}`,
  days_attended: (s) => `${s.daysAttended}`,
  absences: (s) => `${s.sessionsNoShow}`,
  // Punctuality tiles need logged arrival/departure times — with none recorded
  // a "0%" would read as a failing rather than as missing data.
  on_time_rate: (s) =>
    s.arrivalsTotal === 0 ? null : `${Math.round((s.arrivalsOnTime / s.arrivalsTotal) * 100)}%`,
  avg_minutes_late: (s) => (s.arrivalsLate === 0 ? null : `${s.avgMinutesLate}`),
  left_early: (s) => (s.departuresTotal === 0 ? null : `${s.departuresEarly}`),
};

// ── Charts ──────────────────────────────────────────────────────────────────

interface ChartDef {
  /** 520 renders as its own block; 250 pairs up in a row. */
  width: 520 | 250;
  /** Enough data in this period for the chart to say something. */
  available: (s: AttendanceStats) => boolean;
  title: (s: AttendanceStats) => string;
  render: (s: AttendanceStats) => React.ReactNode;
}

/** A single week collapses every by-week series to one point. */
const multiWeek = (s: AttendanceStats) => s.weeklyTrend.length >= 2;

/**
 * The period's sessions as scheduled-vs-actual bars on a clock axis. Recharts
 * has no range bar, so each bar is a two-part stack: a transparent offset from
 * the axis origin, then the visible span.
 */
const TimelineChart: React.FC<{ stats: AttendanceStats }> = ({ stats }) => {
  const rows = stats.sessionTimeline;

  // Domain: earliest start to latest end across scheduled AND actual times,
  // snapped out to whole hours so the ticks read as clock times.
  let lo = Infinity;
  let hi = -Infinity;
  for (const r of rows) {
    lo = Math.min(lo, r.schedStart, r.actualStart ?? r.schedStart);
    hi = Math.max(hi, r.schedEnd, r.actualEnd ?? r.schedEnd);
  }
  const domainMin = Math.floor(lo / 60) * 60;
  const domainMax = Math.ceil(hi / 60) * 60;
  const span = Math.max(60, domainMax - domainMin);
  const ticks = Array.from({ length: Math.floor(span / 60) + 1 }, (_, i) => i * 60);

  const fillFor = (status: TimelineRow['status']) =>
    status === 'attended' ? SCHEDULED : status === 'absent' ? ABSENT : UNLOGGED;

  const data = rows.map((r) => ({
    label: r.label,
    status: r.status,
    schedOffset: r.schedStart - domainMin,
    schedLen: Math.max(0, r.schedEnd - r.schedStart),
    actOffset: r.actualStart === null ? 0 : r.actualStart - domainMin,
    actLen:
      r.actualStart === null || r.actualEnd === null
        ? 0
        : Math.max(0, r.actualEnd - r.actualStart),
  }));

  // Hand-rolled legend: the offset bars are structural and the scheduled bar
  // recolours per row, so a recharts-derived legend would be untruthful — and
  // this version of recharts does not accept an explicit payload.
  const legend = [
    { label: 'Scheduled', colour: SCHEDULED },
    { label: 'Attended', colour: ATTENDED },
    ...(rows.some((r) => r.status === 'absent') ? [{ label: 'Absent', colour: ABSENT }] : []),
    ...(rows.some((r) => r.status === 'unlogged') ? [{ label: 'Not logged', colour: UNLOGGED }] : []),
  ];

  return (
    <>
      <BarChart
        layout="vertical"
        width={520}
        height={Math.max(120, rows.length * 30 + 40)}
        data={data}
        barGap={1}
        margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
        <XAxis
          type="number"
          domain={[0, span]}
          ticks={ticks}
          tickFormatter={(v: number) => hhmm(domainMin + v)}
          stroke={AXIS}
          fontSize={10}
        />
        <YAxis type="category" dataKey="label" width={104} stroke={AXIS} fontSize={10} />
        <Bar dataKey="schedOffset" stackId="sched" fill="transparent" isAnimationActive={false} />
        <Bar dataKey="schedLen" stackId="sched" fill={SCHEDULED} isAnimationActive={false}>
          {data.map((d, i) => <Cell key={i} fill={fillFor(d.status)} />)}
        </Bar>
        <Bar dataKey="actOffset" stackId="act" fill="transparent" isAnimationActive={false} />
        <Bar dataKey="actLen" stackId="act" fill={ATTENDED} isAnimationActive={false} />
      </BarChart>
      <div className="attendance-chart__legend">
        {legend.map((l) => (
          <span key={l.label} className="attendance-chart__legend-item">
            <i style={{ background: l.colour }} />
            {l.label}
          </span>
        ))}
      </div>
    </>
  );
};

const CHARTS: Record<AttendanceChartKey, ChartDef> = {
  session_timeline: {
    width: 520,
    available: (s) => s.sessionTimeline.length > 0 && s.sessionTimeline.length <= TIMELINE_MAX_ROWS,
    title: () => 'Session timeline — scheduled vs attended',
    render: (s) => <TimelineChart stats={s} />,
  },

  weekly_trend: {
    width: 520,
    available: multiWeek,
    title: () => 'Weekly attendance rate',
    render: (s) => (
      <LineChart width={520} height={200} data={s.weeklyTrend} margin={{ top: 10, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="week" stroke={AXIS} fontSize={11} />
        <YAxis stroke={AXIS} fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
        <Line type="monotone" dataKey="rate" stroke={ATTENDED} strokeWidth={2} dot isAnimationActive={false} />
      </LineChart>
    ),
  },

  hours_per_week: {
    width: 520,
    available: (s) => multiWeek(s) && s.hoursPerWeek.some((h) => h.scheduled > 0 || h.attended > 0),
    title: () => 'Hours per week — scheduled vs attended',
    render: (s) => (
      <BarChart width={520} height={200} data={s.hoursPerWeek} margin={{ top: 10, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="week" stroke={AXIS} fontSize={11} />
        <YAxis stroke={AXIS} fontSize={11} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="scheduled" name="Scheduled" fill={SCHEDULED} isAnimationActive={false} />
        <Bar dataKey="attended" name="Attended" fill={ATTENDED} isAnimationActive={false} />
      </BarChart>
    ),
  },

  hours_per_day: {
    width: 520,
    available: (s) => s.hoursPerDay.some((h) => h.scheduled > 0),
    title: () => 'Hours per day — scheduled vs attended',
    render: (s) => (
      <BarChart width={520} height={200} data={s.hoursPerDay} margin={{ top: 10, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="day" stroke={AXIS} fontSize={11} />
        <YAxis stroke={AXIS} fontSize={11} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="scheduled" name="Scheduled" fill={SCHEDULED} isAnimationActive={false} />
        <Bar dataKey="attended" name="Attended" fill={ATTENDED} isAnimationActive={false} />
      </BarChart>
    ),
  },

  punctuality: {
    width: 520,
    available: (s) => s.arrivalsTotal > 0 || s.departuresTotal > 0,
    title: (s) => `Punctuality — within ${s.graceMinutes} minutes of the scheduled time`,
    render: (s) => {
      // Drop the departure row when sign-out times are not recorded, rather
      // than printing an empty bar that reads as "never left".
      const data = s.punctuality.filter((p) => p.name === 'Arrival' || s.departuresTotal > 0);
      return (
        <BarChart
          layout="vertical"
          width={520}
          height={data.length > 1 ? 160 : 120}
          data={data}
          margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
          <XAxis type="number" stroke={AXIS} fontSize={11} allowDecimals={false} />
          <YAxis type="category" dataKey="name" width={78} stroke={AXIS} fontSize={11} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="early" stackId="a" name="Before scheduled time" fill={EARLY} isAnimationActive={false} />
          <Bar dataKey="onTime" stackId="a" name="On time" fill={ON_TIME} isAnimationActive={false} />
          <Bar dataKey="late" stackId="a" name="After scheduled time" fill={LATE} isAnimationActive={false} />
        </BarChart>
      );
    },
  },

  outcome: {
    width: 250,
    available: (s) => s.outcome.length > 0,
    title: () => 'Outcomes',
    render: (s) => (
      <PieChart width={250} height={200}>
        <Pie data={s.outcome} dataKey="value" nameKey="name" cx="50%" cy="46%" outerRadius={62} label isAnimationActive={false}>
          {s.outcome.map((d) => <Cell key={d.name} fill={d.fill} />)}
        </Pie>
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    ),
  },

  by_day: {
    width: 250,
    available: (s) => multiWeek(s) && s.byDayOfWeek.some((d) => d.scheduled > 0),
    title: () => 'Attendance by day',
    render: (s) => (
      <BarChart width={250} height={200} data={s.byDayOfWeek} margin={{ top: 10, right: 8, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="day" stroke={AXIS} fontSize={10} />
        <YAxis stroke={AXIS} fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
        <Bar dataKey="rate" name="Attendance %" fill={ATTENDED} isAnimationActive={false}>
          <LabelList dataKey="rate" position="top" fontSize={9} formatter={(v: any) => (Number(v) > 0 ? `${v}%` : '')} />
        </Bar>
      </BarChart>
    ),
  },
};

/** Canonical print order — a template key order does not reshuffle the page. */
const CHART_ORDER: AttendanceChartKey[] = [
  'session_timeline', 'weekly_trend', 'hours_per_week', 'hours_per_day',
  'punctuality', 'outcome', 'by_day',
];

const TILE_ORDER: AttendanceTileKey[] = [
  'attendance_rate', 'sessions_attended', 'sessions_scheduled',
  'hours_attended', 'hours_scheduled', 'days_attended', 'absences',
  'on_time_rate', 'avg_minutes_late', 'left_early',
];

// ── Component ───────────────────────────────────────────────────────────────

const AttendanceSection: React.FC<Props> = ({ stats, showStats, showCharts, tiles, chartKeys }) => {
  // Omit-when-empty: no schedule in the period → the section renders nothing,
  // consistent with the header fields' "no data → row omitted" rule.
  if (!stats.hasSchedule) return null;

  const selectedTiles = new Set(tiles ?? DEFAULT_ATTENDANCE_TILES);
  const resolvedTiles = TILE_ORDER
    .filter((key) => selectedTiles.has(key))
    .flatMap((key) => {
      const value = TILE_VALUES[key](stats);
      return value === null ? [] : [{ key, label: ATTENDANCE_TILE_LABELS[key], value }];
    });

  const selectedCharts = new Set(chartKeys ?? DEFAULT_ATTENDANCE_CHARTS);
  const visibleCharts = CHART_ORDER.filter((key) => selectedCharts.has(key) && CHARTS[key].available(stats));
  const wideCharts = visibleCharts.filter((key) => CHARTS[key].width === 520);
  const narrowCharts = visibleCharts.filter((key) => CHARTS[key].width === 250);

  const showTileBlock = showStats && resolvedTiles.length > 0;
  const showChartBlock = showCharts && visibleCharts.length > 0;
  // Nothing survived the data checks in either group — drop the heading too.
  if (!showTileBlock && !showChartBlock) return null;

  const renderChart = (key: AttendanceChartKey) => {
    const def = CHARTS[key];
    return (
      <div key={key} className="attendance-chart">
        <div className="attendance-chart__title">{def.title(stats)}</div>
        {def.render(stats)}
      </div>
    );
  };

  return (
    <div className="attendance-section">
      <div className="attendance-section__heading">Attendance</div>

      {showTileBlock && (
        <div className="attendance-section__tiles">
          {resolvedTiles.map((t) => (
            <div key={t.key} className="attendance-tile">
              <div className="attendance-tile__value">{t.value}</div>
              <div className="attendance-tile__label">{t.label}</div>
            </div>
          ))}
        </div>
      )}

      {showChartBlock && (
        <div className="attendance-section__charts">
          {wideCharts.map(renderChart)}
          {narrowCharts.length > 0 && (
            <div className="attendance-section__charts-row">
              {narrowCharts.map(renderChart)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AttendanceSection;
